import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  Stack,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { EnhancedTable, type TableColumn, type TableAction, type FilterConfig } from '../../components/ui/EnhancedTable';
import { AppButton, AppIconButton, AppListPage, AppTextField } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { providerTenantsService } from '../../services/ProviderTenantsService';
import { providerOrdersService } from '../../services/ProviderOrdersService';
import { opsDevicesService } from '../../services/OpsDevicesService';
import type {
  Order,
  CreateOrderRequest,
  OrderStatus,
  OrderListQuery,
  OpsDeviceCategory,
} from '@cnapse/shared/schemas.js';
import { getDeviceCategoryOrderLabel, buildOrderLineItem, getDeviceSkuCatalogEntry } from '@cnapse/shared';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_OPTIONS,
  OrderStatusChip,
} from './orderStatusDisplay';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

// ─── create order form ───────────────────────────────────────────────────────

interface TenantOption {
  id: string;
  name: string;
}

interface CreateOrderFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

const REQUEST_CATEGORIES: Array<{ value: OpsDeviceCategory; label: string }> = [
  { value: 'location-anchor', label: getDeviceCategoryOrderLabel('location-anchor') },
  { value: 'location-tag', label: getDeviceCategoryOrderLabel('location-tag') },
  { value: 'waferid-camera', label: getDeviceCategoryOrderLabel('waferid-camera') },
  { value: 'efem-gateway', label: getDeviceCategoryOrderLabel('efem-gateway') },
  { value: 'barcode-scanner', label: getDeviceCategoryOrderLabel('barcode-scanner') },
  { value: 'handheld-barcode-scanner', label: getDeviceCategoryOrderLabel('handheld-barcode-scanner') },
  { value: 'location-gateway', label: getDeviceCategoryOrderLabel('location-gateway') },
];

function CreateOrderForm({ open, onClose, onSuccess }: CreateOrderFormProps) {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const [inStockByCategory, setInStockByCategory] = useState<Partial<Record<OpsDeviceCategory, number>>>({});
  const [requestedByCategory, setRequestedByCategory] = useState<Partial<Record<OpsDeviceCategory, number>>>({});
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const [tenantId, setTenantId] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [externalReference, setExternalReference] = useState('');
  const [expectedShipDate, setExpectedShipDate] = useState('');
  const [notes, setNotes] = useState('');

  const loadAvailability = useCallback(async () => {
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    try {
      const res = await opsDevicesService.getFleetStats();
      const byCategory = res.data.byCategory ?? {};
      const inStock: Partial<Record<OpsDeviceCategory, number>> = {};
      REQUEST_CATEGORIES.forEach((c) => {
        inStock[c.value] = byCategory[c.value]?.inStock ?? 0;
      });
      setInStockByCategory(inStock);
      setLastCheckedAt(new Date());
    } catch (e) {
      setAvailabilityError(e instanceof Error ? e.message : 'Failed to load availability');
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      providerTenantsService.getTenants().then((list) => setTenants(list.map((t) => ({ id: t.id, name: t.name })))),
      loadAvailability(),
    ])
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load form data'))
      .finally(() => setLoading(false));
  }, [open, loadAvailability]);

  const handleClose = () => {
    setTenantId('');
    setOrderDate(new Date().toISOString().slice(0, 10));
    setExternalReference('');
    setExpectedShipDate('');
    setNotes('');
    setRequestedByCategory({});
    setError(null);
    setAvailabilityError(null);
    onClose();
  };

  const handleSubmit = async (openAllocation: boolean) => {
    if (!tenantId || !orderDate) {
      setError('Tenant and order date are required.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const lineItems = requestedRows
      .filter((row) => row.requested > 0)
      .map((row) => buildOrderLineItem(row.value, row.requested));

    const payload: CreateOrderRequest = {
      tenantId: tenantId as import('@cnapse/shared/data.types.js').Uuid,
      orderDate,
      externalReference: externalReference || undefined,
      expectedShipDate: expectedShipDate || undefined,
      notes: notes || undefined,
      lineItems: lineItems.length > 0 ? lineItems : undefined,
    };

    try {
      const res = await providerOrdersService.createOrder(payload);
      const createdOrderReference = res.data.order.orderReference;
      if (openAllocation) {
        window.history.pushState({}, '', `/provider/orders/${encodeURIComponent(createdOrderReference)}?addDevices=1`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        onSuccess(`Order ${createdOrderReference} created. Continue with device allocation.`);
      } else {
        onSuccess(`Order ${createdOrderReference} created.`);
      }
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const requestedRows = REQUEST_CATEGORIES.map((category) => {
    const requested = requestedByCategory[category.value] ?? 0;
    const available = inStockByCategory[category.value] ?? 0;
    const shortfall = Math.max(0, requested - available);
    const sku = getDeviceSkuCatalogEntry(category.value)?.sku ?? '—';
    return {
      ...category,
      sku,
      requested,
      available,
      shortfall,
    };
  });

  const totalRequested = requestedRows.reduce((sum, row) => sum + row.requested, 0);
  const hasShortfall = requestedRows.some((row) => row.shortfall > 0);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>New Order</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {availabilityError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Availability check is currently unavailable: {availabilityError}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3} sx={{ pt: 1 }}>
            {/* Left column: order fields */}
            <Stack spacing={2.5}>
            <AppTextField
              label="Tenant *"
              select
              fullWidth
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </AppTextField>

            <AppTextField
              label="Order Date *"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />

            <AppTextField
              label="External CRM Reference"
              fullWidth
              value={externalReference}
              onChange={(e) => setExternalReference(e.target.value)}
              placeholder="e.g. Zoho deal ID or HubSpot order ID"
            />

            <AppTextField
              label="Expected Ship Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={expectedShipDate}
              onChange={(e) => setExpectedShipDate(e.target.value)}
            />

            <AppTextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            </Stack>

            {/* Right column: availability matrix */}
            <Box sx={{ pt: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Device Requests and Live Availability
              </Typography>

              <Box
                display="grid"
                gridTemplateColumns="minmax(140px, 1fr) 100px minmax(170px, 1fr) 120px 120px 130px"
                gap={1}
                alignItems="center"
                sx={{ mb: 1, px: 1, py: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="caption" fontWeight={700}>SKU</Typography>
                <Typography variant="caption" fontWeight={700}>Category</Typography>
                <Typography variant="caption" fontWeight={700}>Requested</Typography>
                <Typography variant="caption" fontWeight={700}>Available</Typography>
                <Typography variant="caption" fontWeight={700}>Status</Typography>
              </Box>

              <Stack spacing={1}>
                {requestedRows.map((row) => (
                  <Box
                    key={row.value}
                    display="grid"
                    gridTemplateColumns="minmax(140px, 1fr) 100px minmax(170px, 1fr) 120px 120px 130px"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                      {row.sku}
                    </Typography>
                    <Typography variant="body2">{row.label}</Typography>
                    <AppTextField
                      size="small"
                      type="number"
                      value={row.requested}
                      onChange={(e) => {
                        const next = Math.max(0, Number(e.target.value || 0));
                        setRequestedByCategory((prev) => ({ ...prev, [row.value]: next }));
                      }}
                      inputProps={{ min: 0 }}
                    />
                    <Typography variant="body2" fontFamily="monospace">
                      {row.available}
                    </Typography>
                    {row.shortfall > 0 ? (
                      <Chip size="small" color="warning" label={`SHORTFALL (-${row.shortfall})`} />
                    ) : (
                      <Chip size="small" color="success" label="OK" />
                    )}
                  </Box>
                ))}
              </Stack>

              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Auto-verify ON
                  {lastCheckedAt ? ` • Last checked: ${lastCheckedAt.toLocaleTimeString()}` : ''}
                </Typography>
                <AppButton
                  size="small"
                  variant="text"
                  onClick={() => void loadAvailability()}
                  startIcon={availabilityLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                  disabled={availabilityLoading}
                >
                  Refresh Availability
                </AppButton>
              </Box>

              {totalRequested > 0 && (
                <Alert severity={hasShortfall ? 'warning' : 'success'} sx={{ mt: 1.5 }}>
                  {hasShortfall
                    ? 'Some categories have shortfall. You can still save the draft and complete allocation in Order Detail.'
                    : 'All requested quantities are currently available.'}
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="outlined"
          onClick={() => void handleSubmit(false)}
          disabled={submitting || loading}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Save Draft
        </AppButton>
        <AppButton
          variant="contained"
          onClick={() => void handleSubmit(true)}
          disabled={submitting || loading || !tenantId || !orderDate}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Confirm and Allocate
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [tableFilters, setTableFilters] = useState<Record<string, string>>({ status: '' });
  const [searchTerm, setSearchTerm] = useState('');

  // Create form panel
  const [createOpen, setCreateOpen] = useState(false);

  const filterOptions = useMemo((): FilterConfig[] => [
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: ORDER_STATUS_OPTIONS.map((s) => ({ label: ORDER_STATUS_LABELS[s], value: s })),
    },
  ], []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: OrderListQuery = { page, limit: pageSize };
      if (tableFilters.status) query.status = tableFilters.status as OrderStatus;
      if (searchTerm.trim()) query.search = searchTerm.trim();
      const res = await providerOrdersService.listOrders(query);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, tableFilters.status, searchTerm]);

  useEffect(() => {
    void load();
  }, [load]);

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void load()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [load, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const columns: TableColumn<Order>[] = [
    {
      field: 'orderReference',
      headerName: 'Order Reference',
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
          {row.orderReference}
        </Typography>
      ),
    },
    {
      field: 'tenantName',
      headerName: 'Tenant',
      renderCell: (row) => (
        <Typography variant="body2">{row.tenantName ?? '—'}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (row) => <OrderStatusChip status={row.status} />,
    },
    {
      field: 'orderDate',
      headerName: 'Order Date',
      renderCell: (row) => <Typography variant="body2">{formatDate(row.orderDate)}</Typography>,
    },
    {
      field: 'actualShipDate',
      headerName: 'Shipped',
      renderCell: (row) => <Typography variant="body2">{formatDate(row.actualShipDate)}</Typography>,
    },
    {
      field: 'externalReference',
      headerName: 'CRM Ref',
      renderCell: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.externalReference ?? '—'}
        </Typography>
      ),
    },
  ];

  const rowActions: TableAction<Order>[] = [
    {
      label: 'View Detail',
      onClick: (row) => navigateTo(`/provider/orders/${row.orderReference}`),
    },
  ];

  return (
    <AppListPage>
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <EnhancedTable<Order>
        columns={columns}
        data={orders}
        totalCount={total}
        loading={loading}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        filters={tableFilters}
        filterOptions={filterOptions}
        onFilterChange={(filters) => {
          setTableFilters(filters as Record<string, string>);
          setPage(1);
        }}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search by order reference, tenant, or CRM ref"
        onSearchChange={handleSearchChange}
        actions={rowActions}
        onRowClick={(row) => navigateTo(`/provider/orders/${row.orderReference}`)}
        getRowId={(row) => row.id}
        onAdd={() => setCreateOpen(true)}
        addLabel="New order"
        emptyStateTitle="No orders found"
        emptyStateDescription="Adjust filters or create a new order."
        stickyHeader
        maxHeight="100%"
      />

      <CreateOrderForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={(msg) => { setSuccessMsg(msg); void load(); }}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        message={successMsg}
      />
    </AppListPage>
  );
}
